/*
 * Docs:
 * https://marmelab.com/react-admin/Theming.html#using-a-custom-layout
 */
import React, { Component, createElement, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { createMuiTheme, MuiThemeProvider, withStyles, } from '@material-ui/core/styles';
import Hidden from '@material-ui/core/Hidden';
import compose from 'recompose/compose';
import { defaultTheme, Menu, Notification, Sidebar } from 'react-admin';
import AppBar from './AppBar';
import { getDecodedToken } from "../token";
import {PaymentBanner} from '../components/PaymentBanner';

const styles = theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    zIndex: 1,
    minHeight: '100vh',
    backgroundColor: theme.palette.background.default,
    position: 'relative',
  },
  rootPadded: {
    paddingTop: '2rem',
  },
  appFrame: {
    display: 'flex',
    flexDirection: 'column',
  },
  contentWithSidebar: {
    display: 'flex',
    flexGrow: 1,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 2,
    padding: `${theme.spacing(3)}px 0`,
    [theme.breakpoints.up('xs')]: {
      marginTop: '4em',
    },
    [theme.breakpoints.down('xs')]: {
      marginTop: '0',
    },
    '&.withBanner': {
      [theme.breakpoints.up('xs')]: {
        marginTop: 'calc(4em + 32px)',
      },
      [theme.breakpoints.down('xs')]: {
        marginTop: '32px',
      },
    }
  },
  topMenu: {
    width: '100%',
    top: 0,
    right: 0,
    left: 'auto',
    position: 'fixed',
    zIndex: 1100
  }
});

const sanitizeRestProps = ({staticContext, ...props}) => props;

const Layout = ({
                  appBar,
                  children,
                  classes,
                  className,
                  customRoutes,
                  dashboard,
                  logout,
                  menu,
                  notification,
                  open,
                  title,
                  ...props
                }) => {

  const token = getDecodedToken();

  const {
      status,
      subscription_type: subscriptionType,
      trial_or_active_period_ends: trialOrActivePeriodEnds,
      payment_method: paymentMethod
    } = token || {};
  const [isPaymentBannerVisible, setIsPaymentBannerVisible] = useState(false);

  return (
    <div
      className={classnames('layout', classes.root, className && classes.rootPadded)}
      {...sanitizeRestProps(props)}>
      <div className={classes.appFrame}>
        <div className={classes.topMenu}>
          {token && <PaymentBanner status={status}
                         subscriptionType={subscriptionType}
                         trialOrActivePeriodEnds={trialOrActivePeriodEnds}
                         onVisibleStateChange={setIsPaymentBannerVisible}
                         paymentMethod={paymentMethod}
          />}
          <Hidden xsDown>
            {createElement(appBar, {title, open, logout})}
          </Hidden>
        </div>
        <main className={classes.contentWithSidebar}>
          <Hidden xsUp>
            <Sidebar>
              {createElement(menu, {
                logout,
                hasDashboard: !!dashboard,
              })}
            </Sidebar>
          </Hidden>
          <div className={`${classes.content} ${isPaymentBannerVisible ? 'withBanner' : ''}`}>{children}</div>
        </main>
        {createElement(notification)}
      </div>

    </div>
  )
};

const componentPropType = PropTypes.oneOfType([
  PropTypes.func,
  PropTypes.string,
]);

Layout.propTypes = {
  appBar: componentPropType,
  children: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  classes: PropTypes.object,
  className: PropTypes.string,
  customRoutes: PropTypes.array,
  dashboard: componentPropType,
  logout: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.func,
    PropTypes.string,
  ]),
  menu: componentPropType,
  notification: componentPropType,
  open: PropTypes.bool,
  title: PropTypes.node.isRequired,
};

Layout.defaultProps = {
  appBar: AppBar,
  menu: Menu,
  notification: Notification,
};

const mapStateToProps = state => ({
  open: state.admin.ui.sidebarOpen,
});

const EnhancedLayout = compose(
  connect(
    mapStateToProps,
    {} // Avoid connect passing dispatch in props
  ),
  withStyles(styles)
)(Layout);

export class CoiLayout extends Component {
  constructor(props) {
    super(props);
    this.theme = createMuiTheme(props.theme);
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.theme !== this.props.theme) {
      this.theme = createMuiTheme(nextProps.theme);
    }
  }

  render() {
    const {...rest} = this.props;
    return (
      <MuiThemeProvider theme={this.theme}>
        <EnhancedLayout {...rest} />
      </MuiThemeProvider>
    );
  }
}

CoiLayout.propTypes = {
  theme: PropTypes.object,
};

CoiLayout.defaultProps = {
  theme: defaultTheme,
};
